import { BffApiClientMolecule } from "@/app/api-client/store"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { action, atom, computed, effect, sleep, wrap } from "@reatom/core"
import { molecule } from "bunshi"

interface MintedData {
  minted: number
  myMints?: number
}

/**
 * Molecule for batch-fetching minted counts for multiple drops.
 * Provides a shared cache that all drop items can read from.
 * Automatically refetches every 5 seconds to keep data fresh.
 */
export const BatchMintedDataMoleculeReatom = molecule((mol) => {
  const bffApiClient = mol(BffApiClientMolecule)
  const { unionAddress } = mol(WalletConnectMoleculeReatom)

  // Map of collectionId -> minted data
  const mintedDataMapAtom = atom<Record<string, MintedData>>(
    {},
    "mintedDataMapAtom",
  )

  const isLoadingAtom = atom(false, "isLoadingAtom")

  // Store the list of collection IDs we're tracking
  const trackedIdsAtom = atom<CollectionId[]>([], "trackedIdsAtom")

  // Refetch trigger
  const refetchTriggerAtom = atom(0, "refetchTriggerAtom")

  // Action to initialize cache with server-side data
  const initializeCache = action(
    (drops: Array<{ id: CollectionId; minted?: number }>) => {
      const newMap: Record<string, MintedData> = {}
      for (const drop of drops) {
        if (drop.minted !== undefined) {
          newMap[drop.id] = {
            minted: drop.minted,
            myMints: 0,
          }
        }
      }

      // Only set if we have data
      if (Object.keys(newMap).length > 0) {
        mintedDataMapAtom.set(newMap)
      }
    },
    "initializeCache",
  )

  // Action to fetch minted data for multiple drops
  const fetchBatchMinted = action(async (collectionIds: CollectionId[]) => {
    if (collectionIds.length === 0) return

    // Update tracked IDs
    trackedIdsAtom.set(collectionIds)

    isLoadingAtom.set(true)

    try {
      const address = unionAddress()

      const response = await wrap(
        bffApiClient.api.drops.batch.minted.$post({
          json: {
            ids: collectionIds,
            owner: address,
          },
        }),
      )

      if (!response.ok) {
        throw new Error("Failed to fetch batch minted data")
      }

      const data = await wrap(response.json())

      // Update map with results
      const newMap: Record<string, MintedData> = {}
      for (const result of data.results) {
        newMap[result.id] = {
          minted: result.minted,
          myMints: result.myMints,
        }
      }

      mintedDataMapAtom.set(newMap)
    } catch (error) {
      console.error("Error fetching batch minted data:", error)
    } finally {
      isLoadingAtom.set(false)
    }
  }, "fetchBatchMinted")

  // Refetch effect - refetches every 5 seconds
  const refetchEffect = effect(async () => {
    while (true) {
      await wrap(sleep(5000))

      const ids = trackedIdsAtom()
      if (ids.length > 0) {
        // Refetch without setting loading state
        const address = unionAddress()

        try {
          const response = await wrap(
            bffApiClient.api.drops.batch.minted.$post({
              json: {
                ids,
                owner: address,
              },
            }),
          )

          if (!response.ok) {
            throw new Error("Failed to refetch batch minted data")
          }

          const data = await wrap(response.json())

          // Update map with results
          const newMap: Record<string, MintedData> = {}
          for (const result of data.results) {
            newMap[result.id] = {
              minted: result.minted,
              myMints: result.myMints,
            }
          }

          mintedDataMapAtom.set(newMap)
        } catch (error) {
          console.error("Error refetching batch minted data:", error)
        }
      }
    }
  }, "refetchEffect")

  // Computed to get minted data for a specific collection
  const getMintedData = (collectionId: CollectionId) =>
    computed(() => {
      const map = mintedDataMapAtom()
      return map[collectionId]
    }, `getMintedData-${collectionId}`)

  return {
    mintedDataMapAtom,
    isLoadingAtom,
    fetchBatchMinted,
    getMintedData,
    refetchEffect,
  }
})
